/* ============================================================================
 * FITNESS CLASS – Unified, Robust Type Definitions
 * ----------------------------------------------------------------------------
 * Covers every instructor discipline, schedule pattern, pricing model and
 * delivery format your platform supports today – while remaining open to
 * growth tomorrow.
 *
 * Copy into:  src/types/fitness-class.types.ts
 * ---------------------------------------------------------------------------- */

// ─────────────────────────────────────────────────────────────────────────────
// 0.  CORE LOOK‑UP CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

export const INSTRUCTOR_TYPES = {
  yogaInstructor: "Yoga Instructor",
  pranayamaCoach: "Pranayama Coach",
  meditationCoach: "Meditation Coach",
  zumbaInstructor: "Zumba Instructor",
  nutritionist: "Nutritionist",
  ayurvedaPractitioner: "Ayurveda Practitioner",
  womenWellnessCoach: "Women Wellness Coach",
  selfDefenseInstructor: "Self‑Defence Instructor",
} as const;

export type InstructorType = keyof typeof INSTRUCTOR_TYPES;

export const INSTRUCTOR_CLASS_CATEGORIES: Record<
  InstructorType,
  readonly string[]
> = {
  yogaInstructor: [
    "Hatha Yoga",
    "Vinyasa Flow",
    "Ashtanga",
    "Yin Yoga",
    "Power Yoga",
    "Restorative Yoga",
    "Prenatal Yoga",
    "Chair Yoga",
    "Hot Yoga",
    "Kundalini",
  ],
  pranayamaCoach: [
    "Basic Breathing",
    "Advanced Pranayama",
    "Therapeutic Breathing",
    "Meditation Breathing",
    "Stress Relief Breathing",
    "Energy Breathing",
  ],
  meditationCoach: [
    "Mindfulness Meditation",
    "Guided Meditation",
    "Mantra Meditation",
    "Chakra Meditation",
    "Walking Meditation",
    "Sleep Meditation",
    "Loving‑Kindness",
  ],
  zumbaInstructor: [
    "Classic Zumba",
    "Zumba Gold",
    "Zumba Toning",
    "Aqua Zumba",
    "Zumba Kids",
    "Zumba Sentao",
    "Strong by Zumba",
  ],
  nutritionist: [
    "Weight Management",
    "Diabetic Nutrition",
    "Sports Nutrition",
    "Pregnancy Nutrition",
    "Child Nutrition",
    "Senior Nutrition",
    "Meal Planning",
  ],
  ayurvedaPractitioner: [
    "Ayurvedic Consultation",
    "Dosha Analysis",
    "Panchakarma Guidance",
    "Herbal Medicine",
    "Ayurvedic Lifestyle",
    "Seasonal Detox",
  ],
  womenWellnessCoach: [
    "Menstrual Health",
    "Pregnancy Wellness",
    "Postpartum Care",
    "Menopause Support",
    "Hormonal Balance",
    "Women's Fitness",
  ],
  selfDefenseInstructor: [
    "Basic Self Defence",
    "Martial Arts",
    "Women's Self Defence",
    "Krav Maga",
    "Kickboxing",
    "Situational Awareness",
  ],

};

// ─────────────────────────────────────────────────────────────────────────────
// 1.  ENUMS & SIMPLE TYPES
// ─────────────────────────────────────────────────────────────────────────────

export enum ClassStatus {
  Published = "published",
  Upcoming = "upcoming",
  Completed = "completed",
  Cancelled = "cancelled",
  Draft = "draft",
}

export enum Difficulty {
  Beginner = "beginner",
  Intermediate = "intermediate",
  Advanced = "advanced",
}

export enum AgeBand {
  Kids = "kids",
  Teens = "teens",
  Adults = "adults",
  Seniors = "seniors",
  All = "all",
}

export enum ClassType {
  Live = "live",
  OnDemand = "onDemand",
  Private = "private",
  Group = "group",
  Workshop = "workshop",
  Challenge = "challenge",
}

// ─────────────────────────────────────────────────────────────────────────────
// 2.  SCHEDULE (discriminated union)
// ─────────────────────────────────────────────────────────────────────────────

export type SingleSchedule = {
  kind: "single";
  date: string; // ISO 8601 e.g. "2025‑08‑01T10:00:00+05:30"
  durationMin: number; // minutes
  timeZone?: string; // default Asia/Kolkata
};

export type RecurringSchedule = {
  kind: "recurring";
  startDate: string; // ISO
  endDate?: string; // ISO (open‑ended if undefined)
  /** 0=Sun … 6=Sat. Example: [1,3,5] for Mon/Wed/Fri */
  weekdayMask: number[];
  startTime: string; // "07:00"
  durationMin: number; // minutes
  totalSessions?: number;
  timeZone?: string;
};

export type CourseSchedule = {
  kind: "course";
  /** List of ISO 8601 datetimes for each session */
  sessionDates: string[];
  durationMin: number;
  timeZone?: string;
};

export type FitnessClassSchedule = SingleSchedule | RecurringSchedule | CourseSchedule;

// ─────────────────────────────────────────────────────────────────────────────
// 3.  PRICING (discriminated union)
// ─────────────────────────────────────────────────────────────────────────────

export type PayPerSession = {
  mode: "payPerSession";
  sessionPrice: number; // INR
};

export type RecurringClassPricing = {
  mode: "recurringClass";
  sessionPrice: number; // INR - price for individual session
  packagePrice: number; // INR - price for full recurring package
  totalSessions: number; // number of sessions in the package
  discountPct?: number; // discount percentage for package vs individual sessions
};

export type ClassPack = {
  mode: "classPack";
  classesIncluded: number;
  totalPrice: number; // INR
  discountPct?: number;
  expiresInDays?: number;
};

export type SubscriptionPass = {
  mode: "subscription";
  durationDays: number;
  totalPrice: number; // INR
  unlimited: boolean;
  maxSessionsPerDay?: number;
};

export type CoursePrice = {
  mode: "course";
  totalSessions: number;
  totalPrice: number; // INR
  certificateProvided: boolean;
};

export type ConsultationPrice = {
  mode: "consultation";
  consultationType: "initial" | "followUp" | "specialized";
  durationMin: number;
  price: number; // INR
  includesReport: boolean;
  includesFollowUp: boolean;
};

export type Pricing =
  | PayPerSession
  | RecurringClassPricing
  | ClassPack
  | SubscriptionPass
  | CoursePrice
  | ConsultationPrice;

// ─────────────────────────────────────────────────────────────────────────────
// 4.  INSTRUCTOR‑SPECIFIC EXTENSIONS
// ─────────────────────────────────────────────────────────────────────────────

export interface YogaClassSpecifics {
  yogaStyle: string;
  physicalIntensity: "gentle" | "moderate" | "vigorous";
  propsNeeded?: string[];
  breathworkIncluded?: boolean;
  meditationIncluded?: boolean;
}

export interface PranayamaCoachSpecifics {
  techniques: string[]; // e.g. "Nadi Shodhana", "Kapalabhati"
  therapeuticFocus?: string[]; // stress relief, lung capacity, etc.
  sessionIntensity: "gentle" | "moderate" | "intense";
  includesMeditation?: boolean;
}

export interface MeditationClassSpecifics {
  meditationType: string;
  guidanceStyle: "silent" | "guided" | "music" | "mantra";
  focusAreas?: string[];
  durationMin?: number;
}

export interface ZumbaClassSpecifics {
  zumbaType: string;
  energyLevel: "low" | "moderate" | "high";
  musicGenre?: string[];
}

export interface NutritionConsultationSpecifics {
  consultationType: "assessment" | "mealPlanning" | "followUp";
  includesMealPlan: boolean;
  dietaryRestrictions?: string[];
  healthGoals?: string[];
}

export interface AyurvedaPractitionerSpecifics {
  consultationType: "doshaAnalysis" | "lifestyle" | "detox" | "herbal";
  focuses: string[]; // digestion, immunity, etc.
  herbalRecommendationIncluded?: boolean;
  detoxProgramIncluded?: boolean;
}

export interface WomenWellnessCoachSpecifics {
  lifeStage: "menstrual" | "pregnancy" | "postpartum" | "menopause";
  focusAreas: string[]; // e.g. hormonal balance, fitness
  includesWorkout?: boolean;
  includesNutrition?: boolean;
}

export interface SelfDefenseClassSpecifics {
  techniquesFocused: string[];
  physicalContactLevel: "none" | "light" | "moderate" | "full";
  equipmentProvided?: string[];
}

export interface SeniorWellnessCoachSpecifics {
  programEmphasis: string[]; // joint mobility, balance, etc.
  mobilityLevel: "low" | "moderate" | "high";
  fallPreventionIncluded?: boolean;
  chairExerciseIncluded?: boolean;
}

export interface KidsWellnessCoachSpecifics {
  programType: string[]; // fun movement, mindful games, yoga
  funLevel: 1 | 2 | 3 | 4 | 5;
  parentalInvolvementRequired?: boolean;
  includesCreativePlay?: boolean;
}

/** Full map: instructorType → specialised payload interface */
export type SpecMap = {
  yogaInstructor: YogaClassSpecifics;
  pranayamaCoach: PranayamaCoachSpecifics;
  meditationCoach: MeditationClassSpecifics;
  zumbaInstructor: ZumbaClassSpecifics;
  nutritionist: NutritionConsultationSpecifics;
  ayurvedaPractitioner: AyurvedaPractitionerSpecifics;
  womenWellnessCoach: WomenWellnessCoachSpecifics;
  selfDefenseInstructor: SelfDefenseClassSpecifics;
  seniorWellnessCoach: SeniorWellnessCoachSpecifics;
  kidsWellnessCoach: KidsWellnessCoachSpecifics;
};

// ─────────────────────────────────────────────────────────────────────────────
// 5.  CORE DOMAIN MODEL (generic)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * FitnessClass is generic so TypeScript narrows specializedContent automatically.
 *   FitnessClass<"yogaInstructor">  →  specializedContent: YogaClassSpecifics | undefined
 */
export interface FitnessClass<
  T extends InstructorType = InstructorType,
  S = SpecMap[T],
> {
  // identity
  id: string; // UUID client‑side / _id server‑side
  title: string;
  description: string;

  // instructor
  instructorId: string;
  instructorName: string;
  instructorType: T;
  instructorImage?: string;

  // taxonomy
  category: string; // must come from INSTRUCTOR_CLASS_CATEGORIES
  classType: ClassType;
  difficulty: Difficulty[];
  ageBand: AgeBand;

  // logistics
  schedule: FitnessClassSchedule;
  pricing: Pricing;
  format: "online";
  languages: string[];
  location?: string; // if in‑person
  tags?: string[];

  // capacity
  capacity: number;
  spotsLeft: number;
  minParticipants?: number;
  waitlistEnabled: boolean;

  // accessibility & safety
  accessibility?: {
    wheelchairAccessible?: boolean;
    hearingImpaired?: boolean;
    visuallyImpaired?: boolean;
    languageSupport?: string[];
  };
  safety?: {
    riskLevel?: "low" | "medium" | "high";
    medicalClearanceRequired?: boolean;
    waiverRequired?: boolean;
    insuranceRequired?: boolean;
  };

  // media
  media?: {
    image?: string;
    videoPreview?: string;
    gallery?: string[];
  };

  // extras – marketing & requirements (all optional)
  equipmentProvided?: string[];
  equipmentRequired?: string[];
  prerequisites?: string[];
  requirements?: string[];
  benefits?: string[];
  features?: string[];
  achievements?: string[];
  whatToExpect?: string[];
  whatToAvoid?: string[];

  // reviews summary (detailed reviews stored elsewhere)
  rating: number;
  reviewCount: number;

  // state & audit
  status: ClassStatus;
  isPublished: boolean;
  isLive?: boolean;
  createdAt: Date;
  updatedAt: Date;

  // instructor‑specific payload
  specializedContent?: S;
}

// ─────────────────────────────────────────────────────────────────────────────
// 6.  ALIASES / VIEWS (no field duplication)
// ─────────────────────────────────────────────────────────────────────────────

/** Exactly what sits in Mongo */
export type ClassDoc<T extends InstructorType = InstructorType> =
  FitnessClass<T> & {
    _id: string; // Mongo ObjectId string
  };

/** What your REST/GraphQL API returns to clients */
export type ClassDTO<T extends InstructorType = InstructorType> = Omit<
  FitnessClass<T>,
  "createdAt" | "updatedAt"
>;

/** Shape you use inside React Hook Form / Zod validations */
export type ClassForm<T extends InstructorType = InstructorType> = Omit<
  FitnessClass<T>,
  "id" | "rating" | "reviewCount" | "createdAt" | "updatedAt" | "spotsLeft"
> & {
  draft: boolean; // extra flag for form state
};

// ─────────────────────────────────────────────────────────────────────────────
// 7.  RUNTIME HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Validate that a chosen category matches the instructor type at runtime */
export function isValidCategory(
  instructorType: InstructorType,
  category: string
): boolean {
  return (INSTRUCTOR_CLASS_CATEGORIES[instructorType] || []).includes(category);
}

// ─────────────────────────────────────────────────────────────────────────────
// 8.  FILTER TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type Filters = {
  date: string;
  instructor: string;
  instructorType: InstructorType | "all";
  duration: number;
  difficulty: string;
  level: string;
  category: string;
  format: string;
  classType: ClassType | "all";
  ageGroup: AgeBand | "all";
  price: [number, number];
  timeOfDay: string;
  language: string;
  classLanguages: string;
  specialFeatures: string[];
  location: string;
  instructorExperience: number;
  rating: [number, number];
  capacity: number;
  accessibility: string[];
  medicalClearanceRequired?: boolean;
  parentalConsentRequired?: boolean;
  equipmentProvided?: string[];
  waitlistAvailable?: boolean;
  status?: string; // Filter by class status (published, draft, etc.)
};

export interface ClassFilterBarProps {
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filters: Filters;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  setHasMore?: React.Dispatch<React.SetStateAction<boolean>>;
}
